import { CONSENT_UPDATED_EVENT, CONSENT_SIGNALS_KEY } from './constants';
import type { ConsentModeSignals, ConsentUpdatedEvent } from './types';

/**
 * Internal-only. The module's second dataLayer writer: the plain-object EVENT
 * that tells GTM the consent signals changed.
 *
 * Nothing outside `lib/consent-mode` should call this — go through
 * `consentMode.ts`, which is what guarantees the event only ever follows a real
 * consent update, and only ever follows it.
 *
 * WHY THIS IS A SEPARATE WRITER FROM `gtag.ts`
 * --------------------------------------------
 * One dataLayer, two wire shapes, two audiences:
 *
 *   gtag.ts    → an ARGUMENTS object → read by GOOGLE's tags.
 *                Push a plain object and Google never hears the update.
 *   this file  → a PLAIN OBJECT      → read by GTM's TRIGGER engine.
 *                Push an arguments object and no trigger can ever match it.
 *
 * The shapes are not interchangeable in either direction, and getting either one
 * wrong fails silently — no console error, no failed request, nothing visible in
 * Tag Assistant. One writer per shape is what stops that drifting per call site,
 * and is why this is a file rather than four lines inside `consentMode.ts`
 * (which deliberately touches `window.dataLayer` nowhere at all).
 *
 * WHY NOT `pushToDataLayer` FROM `lib/analytics`
 * ----------------------------------------------
 * The shape would be right and the ownership would be wrong. `lib/analytics`
 * owns the marketing event catalog and is vendored on its own schedule; this
 * module is deliberately independent of it (same note in `gtag.ts`), and this
 * copy would have to pull a second foundation into `app/` to gain four lines of
 * push.
 *
 * TypeScript rendition of landing-page/src/lib/consent-mode/consentEvent.js.
 */

/**
 * Announce a consent change to GTM.
 *
 * The signals are COPIED onto the payload rather than referenced: the very same
 * object was handed to the gtag command a moment earlier, and the dataLayer is a
 * shared bus that any consumer may hold on to. Two entries aliasing one mutable
 * object is the kind of thing that is fine until it is not. Same discipline as
 * `getDefaultConsentModeSignals()` here and `setConsent()` in the foundation.
 *
 * `window.dataLayer` is normally already initialized by the inline snippet in
 * index.html; the `|| []` is defensive for the case where it never ran, exactly
 * as in `gtag.ts`.
 *
 * @param signals The signal set that was just sent to Google.
 */
export function pushConsentEvent(signals: ConsentModeSignals): void {
  if (typeof window === 'undefined') return;

  const payload: ConsentUpdatedEvent = {
    event: CONSENT_UPDATED_EVENT,
    [CONSENT_SIGNALS_KEY]: { ...signals },
  };

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
}
